import api from '../httpClient'

export type ContinuityPerson = { id: number; name: string }
export type ContinuityRevision = {
  id: string
  actor: ContinuityPerson | null
  source: 'human' | 'ai'
  changes: Record<string, { before: unknown; after: unknown }>
  created_at: string
}
export type ContinuityEvidence = {
  excerpt?: string
  explanation?: string
  source_title?: string
  meeting_id?: string
  meeting_start_time?: string
}
export type ContinuityItem = {
  id: string
  kind: 'topic' | 'action'
  text: string
  status: 'open' | 'discussed' | 'completed'
  provenance: 'manual' | 'agreed' | 'suggested'
  version: number
  is_removed: boolean
  owner_id: number | null
  source_recap_id: string | null
  evidence: ContinuityEvidence[]
  created_at: string
  updated_at: string
  edited_by: ContinuityPerson | null
  history: ContinuityRevision[]
}
export type ContinuityRecap = {
  id: string
  meeting_id: string
  breakout_room_id: string | null
  source_kind: 'breakout' | 'meeting' | 'group'
  source_title: string
  meeting_start_time: string | null
  status: 'pending' | 'ready' | 'partial' | 'failed'
  /** Pending because the conversation is still going on, not because processing is under way. */
  in_progress?: boolean
  unavailable_reason?: 'pair_attendance_unconfirmed' | null
  error_message?: string
  can_retry?: boolean
  summary: string
  version: number
  edited_by: ContinuityPerson | null
  history: ContinuityRevision[]
  created_at: string
  updated_at: string
}
export type MentorshipRelationship = {
  id: string
  cohort_id: string
  mentor: ContinuityPerson
  mentee: ContinuityPerson
  is_current: boolean
  can_edit: boolean
  items: ContinuityItem[]
  recaps: ContinuityRecap[]
}
export type ContinuityItemPatch = {
  version: number
  text?: string
  status?: ContinuityItem['status']
  is_removed?: boolean
}

const paramsFor = (cohortId: string) => ({ params: { cohort_id: cohortId } })
const idPath = (id: string) => encodeURIComponent(id)

export const mentorshipContinuity = {
  async list(cohortId: string): Promise<MentorshipRelationship[]> {
    const { data } = await api.get<{ relationships: MentorshipRelationship[] }>('/api/mentorship/relationships/', paramsFor(cohortId))
    return data.relationships
  },
  async get(cohortId: string, id: string): Promise<MentorshipRelationship> {
    const { data } = await api.get<MentorshipRelationship>(`/api/mentorship/relationships/${idPath(id)}/`, paramsFor(cohortId))
    return data
  },
  async addItem(cohortId: string, relationshipId: string, payload: { text: string; kind: ContinuityItem['kind']; owner_id?: number | null; client_key?: string }): Promise<ContinuityItem> {
    const { data } = await api.post<ContinuityItem>(`/api/mentorship/relationships/${idPath(relationshipId)}/items/`, payload, paramsFor(cohortId))
    return data
  },
  async updateItem(cohortId: string, id: string, payload: ContinuityItemPatch): Promise<ContinuityItem> {
    const { data } = await api.patch<ContinuityItem>(`/api/mentorship/items/${idPath(id)}/`, payload, paramsFor(cohortId))
    return data
  },
  async updateRecap(cohortId: string, id: string, payload: { version: number; summary: string }): Promise<ContinuityRecap> {
    const { data } = await api.patch<ContinuityRecap>(`/api/mentorship/recaps/${idPath(id)}/`, payload, paramsFor(cohortId))
    return data
  },
  async retryRecap(cohortId: string, id: string): Promise<ContinuityRecap> {
    const { data } = await api.post<ContinuityRecap>(`/api/mentorship/recaps/${idPath(id)}/retry/`, {}, paramsFor(cohortId))
    return data
  },
}
